import { css } from '@emotion/react';
import {
  type FC, useCallback, useEffect, useRef, useState,
} from 'react';
import settingsMapPlaceholderImage from '../../../../../assets/images/boundarySettingsMapPlaceholder.svg';
import { rgbColorToString } from '../../../_shared/components/colorPicker/colorPicker.helpers';
import { type ColorResult } from '../../../_shared/components/colorPicker/colorPicker.types';
import { useTranslation } from '../../../_shared/utils/hooks';

const mapWrapperStyle = ({ squareSize }: { squareSize?: number }) => css({
  position: 'relative',
  ...(squareSize && {
    height: squareSize,
    width: squareSize,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
});

const mapImageStyle = ({ squareSize }: { squareSize?: number }) => css({
  ...(squareSize ? {
    height: '100%',
    width: 'auto',
    minWidth: '100%',
    objectFit: 'cover',
  } : {
    maxWidth: '100%',
  }),
});

const LineExampleMargin = 25;

const lineExampleStyle = ({ lineWidth, lineColor, fillColor }: { lineWidth: number; lineColor: string; fillColor?: string }) => css({
  position: 'absolute',
  border: `${lineWidth}px solid ${lineColor}`,
  background: fillColor,
  top: LineExampleMargin,
  left: LineExampleMargin,
  bottom: LineExampleMargin,
  right: LineExampleMargin,
  zIndex: 1, // Ensure the line appears above the image
});

const labelPreviewStyle = css({
  position: 'absolute',
  padding: 6,
  borderRadius: 4,
  fontSize: 16,
  fontWeight: 100,
  fontFamily: 'Arial',
  textAlign: 'center',
  zIndex: 2, // Ensure the label appears above the line
  whiteSpace: 'nowrap',
});

type BoundaryPreviewProps = Readonly<{
  className?: string;
  fillColor?: ColorResult;
  labelBackgroundColor?: string;
  labelPreviewText?: React.ReactNode;
  labelTextColor?: string;
  lineColor: ColorResult;
  lineWidth: number;
  showLabel?: boolean;
  squareSize?: number;
}>;

export const BoundaryPreviewComponent: FC<BoundaryPreviewProps> = ({
  className,
  fillColor,
  labelBackgroundColor,
  labelPreviewText,
  labelTextColor,
  lineColor,
  lineWidth,
  showLabel,
  squareSize,
}) => {
  const [t] = useTranslation();
  const textPreviewRef = useRef<HTMLDivElement>(null);
  const linePreviewRef = useRef<HTMLDivElement>(null);
  const [scaleRatio, setScaleRatio] = useState(1);

  const setScale = useCallback(() => {
    const linePreviewElement = linePreviewRef.current;
    const textPreviewElement = textPreviewRef.current;
    if (linePreviewElement && textPreviewElement) {
      const linePreviewWidth = linePreviewElement.offsetWidth;
      const textPreviewWidth = textPreviewElement.offsetWidth;

      const scale = (linePreviewWidth - 4) / textPreviewWidth;
      setScaleRatio(Math.min(scale, 1));
    }
  }, []);

  useEffect(() => {
    setScale();
  }, [labelPreviewText, setScale]);

  const updateTextPreviewRef = useCallback((element: HTMLDivElement|null) => {
    textPreviewRef.current = element;

    setScale();
  }, [setScale]);

  return (
    <div
      className={className}
      css={mapWrapperStyle({ squareSize })}
    >
      <div
        ref={linePreviewRef}
        css={lineExampleStyle({
          lineWidth,
          lineColor: rgbColorToString(lineColor.rgba),
          fillColor: fillColor && rgbColorToString(fillColor.rgba),
        })}
      />
      {showLabel && (
        <div
          ref={updateTextPreviewRef}
          css={labelPreviewStyle}
          style={{
            backgroundColor: labelBackgroundColor,
            color: labelTextColor,
            transform: `scale(${scaleRatio})`,
          }}
        >
          {labelPreviewText}
        </div>
      )}
      <img
        css={mapImageStyle({ squareSize })}
        src={settingsMapPlaceholderImage}
        alt={t('Map Placeholder')}
      />
    </div>
  );
};
